import { useAny } from '@clover-util/use-any';
import { useAll } from '@clover-util/use-all';
import { createHookContext } from '@clover-util/react-context';
import { useMemo } from 'react';
import { useMerchant } from './merchant';
import { useServiceType } from './serviceType';
import { ServiceType } from './serviceType/models';
import { useScheduledOrderDisplay } from './ScheduledOrderDisplay';
import { useMenu, GIFTCARD_CATEGORY_ID } from '../../components/MenuContext/MenuContext';

export const { useMerchantState, MerchantStateProvider } = createHookContext('MerchantState', () => {
  const { status, merchant, isCustomerAuthEnabled, isPerksEnabled } = useMerchant();
  const { menu, loading } = useMenu();
  const { noAvailableTimes } = useScheduledOrderDisplay();
  const { isDineIn, serviceTypes, currentServiceType, hasDineInQueryParam, hasTableId } = useServiceType();

  const scheduledOrderingEnabled = merchant?.scheduledOrdering?.enabled;

  const hasOnlyGiftCardinMenu = !loading ? menu?.hasGiftCard && menu?.categories.length === 1 && menu?.categories[0].id === GIFTCARD_CATEGORY_ID: false;
  const showGiftCardOnlyState = useAny( [
    merchant?.onlineServices?.GIFT_CARDS?.enabled && !merchant?.onlineServices?.ONLINE_ORDERING?.enabled,
    merchant?.onlineServices?.GIFT_CARDS?.enabled && hasOnlyGiftCardinMenu
  ]);
  const isDiningScanToOrder = merchant?.onlineServices?.DINING_SCAN_TO_ORDER?.enabled;

  const dineInService = merchant?.services?.find(service => service.type === ServiceType.DINE_IN);
  const isDineReadOnly = hasDineInQueryParam && (status === 'closed' || !dineInService?.enabled || (isDiningScanToOrder && !hasTableId));

  const enabledServiceTypes = useMemo(() => merchant?.services?.filter(({ enabled } : { enabled: boolean }) => enabled), [merchant]);

  const isInvalidDineIn = useAll([
    enabledServiceTypes?.length === 1,
    !!enabledServiceTypes?.find(service => service.type === ServiceType.DINE_IN),
    !hasDineInQueryParam,
  ]);

  const isAcceptingOrders = useAny([
    status === 'online',
    !!(status === 'closed' && scheduledOrderingEnabled && !noAvailableTimes),
    showGiftCardOnlyState,
  ]) && !isDineReadOnly && !isInvalidDineIn;

  const showDineInState = useAll([
    isDineIn,
    (status === 'online' || status === 'closed'),
    !showGiftCardOnlyState,
  ]);

  const showRunningOrderState = useAll([
    showDineInState,
    !!isDiningScanToOrder,
  ]);

  // Show state where you are able to set a scheduled order
  const showScheduledOrderState = useAll([
    !!scheduledOrderingEnabled,
    !noAvailableTimes,
    !isDineIn,
    !showGiftCardOnlyState,
  ]);

  // Show state where you are able to set a scheduled order but the merchant is closed ex: show modal, no asap orders
  const showScheduledOrderClosedState = useAll([
    status === 'closed',
    showScheduledOrderState
  ]);

  const showCustomerAuthState = useAll([
    isCustomerAuthEnabled,
    !isDineIn,
    !showGiftCardOnlyState,
  ]);

  const showPerksState = useAll([
    showCustomerAuthState,
    isPerksEnabled
  ]);

  const showDeliveryScreen = useAll([
    !showScheduledOrderClosedState,
    !showDineInState,
    serviceTypes.length > 1,
    !!serviceTypes.find(service => service.name === ServiceType.DELIVERY),
    currentServiceType !== ServiceType.DELIVERY
  ]);

  return {
    isAcceptingOrders,
    showGiftCardOnlyState,
    showDineInState,
    showRunningOrderState,
    showScheduledOrderClosedState,
    showCustomerAuthState,
    showScheduledOrderState,
    showPerksState,
    showDeliveryScreen,
    isDineReadOnly,
    isInvalidDineIn
  };
});
